import { defineRecipe } from '@chakra-ui/react';

import {
  buttonGap,
  buttonIconSizePx,
  buttonOutlineBorderWidth,
  buttonPaddingX,
  iconButtonSquareSize,
} from './button.kds';

const iconBoxSize = (size: keyof typeof buttonIconSizePx) => `${buttonIconSizePx[size]}px`;

const filledDisabledStyles = {
  bg: 'neutral.200',
  color: 'neutral.350',
  opacity: 0.8,
  _hover: { bg: 'neutral.200' },
  _active: { bg: 'neutral.200' },
};

const filledSolidStyles = {
  fontWeight: 'medium',
  _disabled: filledDisabledStyles,
};

const outlinedDisabledStyles = {
  borderColor: 'neutral.250',
  color: 'neutral.350',
  bg: 'transparent',
  opacity: 0.8,
  _hover: { bg: 'transparent', borderColor: 'neutral.250' },
  _active: { bg: 'transparent', borderColor: 'neutral.250' },
};

const outlinedStyles = {
  fontWeight: 'medium',
  borderStyle: 'solid',
  bg: 'transparent',
  _disabled: outlinedDisabledStyles,
};

const ghostedDisabledStyles = {
  color: 'neutral.350',
  bg: 'transparent',
  opacity: 0.8,
  _hover: { bg: 'transparent' },
  _active: { bg: 'transparent' },
};

const ghostedStyles = {
  fontWeight: 'medium',
  bg: 'transparent',
  _disabled: ghostedDisabledStyles,
};

/**
 * KDS Button + IconButton — Figma Kintsugi Design System node 1045:13557.
 *
 * NOTE: Sidebar nav items are NOT a button variant. They are a separate Figma
 * component ("Side Nav Component", node 352:2883) and will be implemented as a
 * dedicated NavItem component in @kintsugi/ui when the sidebar migration happens.
 */
export const buttonRecipe = defineRecipe({
  base: {
    borderRadius: 'md',
    fontWeight: 'medium',
  },
  variants: {
    layout: {
      label: {},
      icon: {
        gap: 0,
      },
    },
    variant: {
      solid: filledSolidStyles,
      outline: outlinedStyles,
      ghost: ghostedStyles,
    },
    size: {
      lg: {
        h: '48px',
        minW: '48px',
        px: buttonPaddingX.lg,
        gap: buttonGap.lg,
        textStyle: 'h5.medium',
        _icon: { boxSize: iconBoxSize('lg') },
      },
      md: {
        h: '10',
        minW: '10',
        px: buttonPaddingX.md,
        gap: buttonGap.md,
        textStyle: 'default.medium',
        _icon: { boxSize: iconBoxSize('md') },
      },
      rg: {
        h: '8',
        minW: '8',
        px: buttonPaddingX.rg,
        gap: buttonGap.rg,
        textStyle: 'default.medium',
        _icon: { boxSize: iconBoxSize('rg') },
      },
      sm: {
        h: '6',
        minW: '6',
        px: buttonPaddingX.sm,
        gap: buttonGap.sm,
        textStyle: 'small.medium',
        _icon: { boxSize: iconBoxSize('sm') },
      },
    },
  },
  compoundVariants: [
    // Icon-only — square hit target per Figma IconButton (no horizontal padding)
    { layout: 'icon', size: 'lg', css: iconButtonSquareSize.lg },
    { layout: 'icon', size: 'md', css: iconButtonSquareSize.md },
    { layout: 'icon', size: 'rg', css: iconButtonSquareSize.rg },
    { layout: 'icon', size: 'sm', css: iconButtonSquareSize.sm },
    // Outlined border width scales with size (Figma node 1:14)
    { variant: 'outline', size: 'lg', css: { borderWidth: buttonOutlineBorderWidth.lg } },
    { variant: 'outline', size: 'md', css: { borderWidth: buttonOutlineBorderWidth.md } },
    { variant: 'outline', size: 'rg', css: { borderWidth: buttonOutlineBorderWidth.rg } },
    { variant: 'outline', size: 'sm', css: { borderWidth: buttonOutlineBorderWidth.sm } },
    // Accent (neonGreen)
    {
      variant: 'solid',
      colorPalette: 'neonGreen',
      css: {
        bg: 'neonGreen.500',
        color: 'neutral.500',
        _hover: { bg: 'neonGreen.600' },
        _active: { bg: 'neonGreen.700' },
      },
    },
    {
      variant: 'outline',
      colorPalette: 'neonGreen',
      css: {
        borderColor: 'neonGreen.900',
        color: 'neonGreen.900',
        _hover: { bg: 'neonGreen.100', borderColor: 'neonGreen.900' },
        _active: { bg: 'neonGreen.200', borderColor: 'neonGreen.900' },
      },
    },
    {
      variant: 'ghost',
      colorPalette: 'neonGreen',
      css: {
        color: 'neonGreen.900',
        _hover: { bg: 'neonGreen.100' },
        _active: { bg: 'neonGreen.200' },
      },
    },
    // Primary (brand)
    {
      variant: 'solid',
      colorPalette: 'brand',
      css: {
        bg: 'brand.500',
        // A portfolio's pale primary swaps in dark label text at runtime.
        color: 'brand.contrast',
        _hover: { bg: 'brand.600' },
        _active: { bg: 'brand.700' },
      },
    },
    {
      variant: 'outline',
      colorPalette: 'brand',
      css: {
        borderColor: 'brand.500',
        color: 'brand.text',
        _hover: { bg: 'brand.100', borderColor: 'brand.500' },
        _active: { bg: 'brand.200', borderColor: 'brand.500' },
      },
    },
    {
      variant: 'ghost',
      colorPalette: 'brand',
      css: {
        color: 'brand.text',
        _hover: { bg: 'brand.100' },
        _active: { bg: 'brand.200' },
      },
    },
    // Secondary (neutral)
    {
      variant: 'solid',
      colorPalette: 'neutral',
      css: {
        bg: 'neutral.100',
        color: 'neutral.350',
        _hover: { bg: 'neutral.200' },
        _active: { bg: 'neutral.250', opacity: 0.8 },
      },
    },
    {
      variant: 'outline',
      colorPalette: 'neutral',
      css: {
        borderColor: 'neutral.200',
        color: 'neutral.350',
        _hover: { bg: 'neutral.100', borderColor: 'neutral.200' },
        _active: { bg: 'neutral.200', borderColor: 'neutral.200' },
      },
    },
    {
      variant: 'ghost',
      colorPalette: 'neutral',
      css: {
        color: 'neutral.350',
        _hover: { bg: 'neutral.100' },
        _active: { bg: 'neutral.200' },
      },
    },
    // Destructive (red)
    {
      variant: 'solid',
      colorPalette: 'red',
      css: {
        bg: 'red.700',
        color: 'neutral.0',
        _hover: { bg: 'red.800' },
        _active: { bg: 'red.900' },
      },
    },
    {
      variant: 'outline',
      colorPalette: 'red',
      css: {
        borderColor: 'red.700',
        color: 'red.700',
        _hover: { bg: 'red.100', borderColor: 'red.700' },
        _active: { bg: 'red.200', borderColor: 'red.700' },
      },
    },
    {
      variant: 'ghost',
      colorPalette: 'red',
      css: {
        color: 'red.700',
        _hover: { bg: 'red.100' },
        _active: { bg: 'red.200' },
      },
    },
  ],
  defaultVariants: {
    layout: 'label',
    size: 'rg',
    variant: 'solid',
    colorPalette: 'brand',
  },
});
